import { isNotDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import type { Frequency } from '@/types/common.ts';

const FixingFrequencies = () => {
  const fixingFrequency = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.fixingFrequency,
  );
  const fixingFrequenciesList = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.fixingFrequenciesList,
  );
  const numberOfFixings = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.numberOfFixings,
  );
  const isCustom = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.customFields?.includes('FixingFrequency') ?? false,
  );
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleFixingFrequencies = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Fixing frequency changed',
      isNotDefined(event.target.value) ? 'cleared' : event.target.value,
    );

    return checkTargetAccumulatorWithTradeCapture({
      fixingFrequency: event.target.value as Frequency,
    });
  };

  const handleNumberOfFixings = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Number of fixings changed',
      isNotDefined(value) ? 'cleared' : String(value),
    );

    if (isNotDefined(value)) {
      return null;
    }

    return checkTargetAccumulatorWithTradeCapture({
      numberOfFixings: value,
    });
  };

  return (
    <TargetAccumulatorFormField
      formattedMessageId="targetAccumulator.form.fixingFrequencies"
      extra={
        <Tooltip position="right" contents={<FormattedMessage id="targetAccumulator.form.fixingFrequency.info" />}>
          <div className="badge badge-dismissible">
            <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
          </div>
        </Tooltip>
      }
    >
      <div className="d-flex">
        <Form.Select
          value={fixingFrequency}
          onChange={handleFixingFrequencies}
          size="sm"
          data-e2e="fixing-frequencies-selector"
        >
          {fixingFrequenciesList?.map((mode) => (
            <option key={mode} value={mode}>
              <FormattedMessage id={`targetAccumulator.form.fixingFrequencies.${mode}`} />
            </option>
          ))}
        </Form.Select>
        <div className="w-25 ps-1">
          <SgNumberInput
            valuePosition="start"
            selectAllOnFocus
            className={isCustom ? 'text-info' : undefined}
            data-e2e-is-modified={isCustom}
            value={numberOfFixings}
            maxDecimals={2}
            onChange={handleNumberOfFixings}
            size="sm"
            data-e2e="numberOfFixings"
          />
        </div>
      </div>
    </TargetAccumulatorFormField>
  );
};

export default FixingFrequencies;
